import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, FolderTree, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTeamTree, useReparentTeam } from "@/hooks/queries/use-teams";
import { useUserTeams } from "@/hooks/queries/use-users";
import { DisciplineFormModal } from "@/components/teams/discipline-form-modal";
import { DeleteDisciplineDialog } from "@/components/teams/delete-discipline-dialog";
import { useAuthStore } from "@/stores/auth-store";
import { useToast } from "@/hooks/useToast";
import { getTeamHueColor, getTeamIconPath } from "@/lib/team-hue";
import { TEAM_LEVEL_ORDER, findTeamLineage, flattenTeamTree } from "@/lib/team-hierarchy";
import { cn } from "@/lib/utils";
import type { TeamTreeNode } from "@/lib/api/types";

const STORAGE_KEY = "teams.tree.expanded";

interface TeamTreeSidebarProps {
  currentTeamId?: string;
  className?: string;
  onNavigate?: () => void;
}

interface TeamTreeRowProps {
  node: TeamTreeNode;
  depth: number;
  expandedIds: Set<string>;
  currentTeamId: string | undefined;
  isAdmin: boolean;
  canReparent: boolean;
  userTeamIds: Set<string>;
  dragOverId: string | null;
  focusedTeamId: string | null;
  onToggle: (teamId: string) => void;
  onFocusTeam: (teamId: string) => void;
  onDragStart: (teamId: string) => void;
  onDragEnd: () => void;
  onDragEnter: (teamId: string) => void;
  onDrop: (parentId: string) => void;
  canDropOn: (parentId: string) => boolean;
  registerRowElement: (teamId: string, element: HTMLDivElement | null) => void;
  onNavigate: (() => void) | undefined;
}

const DEPTH_INDENT_CLASSES = ["pl-2", "pl-5", "pl-8", "pl-11", "pl-14"] as const;
const TREE_ITEM_ID_PREFIX = "team-tree-item";

function getTreeItemId(teamId: string): string {
  return `${TREE_ITEM_ID_PREFIX}-${teamId}`;
}

function TeamTreeRow({
  node,
  depth,
  expandedIds,
  currentTeamId,
  isAdmin,
  canReparent,
  userTeamIds,
  dragOverId,
  focusedTeamId,
  onToggle,
  onFocusTeam,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDrop,
  canDropOn,
  registerRowElement,
  onNavigate,
}: TeamTreeRowProps) {
  const [showAddDiscipline, setShowAddDiscipline] = useState(false);
  const [showEditDiscipline, setShowEditDiscipline] = useState(false);
  const [showDeleteDiscipline, setShowDeleteDiscipline] = useState(false);
  const isExpanded = expandedIds.has(node.id);
  const isActive = currentTeamId === node.id;
  const isFocused = focusedTeamId === node.id;
  const isUserTeam = userTeamIds.has(node.id);
  const isDragTarget = dragOverId === node.id && canDropOn(node.id);
  const hueColor = getTeamHueColor(node.name);
  const iconPath = getTeamIconPath(node.name, node.level);
  const canShowChildren = node.children.length > 0;
  const canAddDiscipline = isAdmin && node.level === "product";
  const canManageDiscipline = isAdmin && node.level === "discipline";
  const depthClass = DEPTH_INDENT_CLASSES[Math.min(depth, DEPTH_INDENT_CLASSES.length - 1)];

  return (
    <div>
      <div
        id={getTreeItemId(node.id)}
        ref={(element) => registerRowElement(node.id, element)}
        role="treeitem"
        aria-expanded={canShowChildren ? isExpanded : undefined}
        aria-level={depth + 1}
        aria-selected={isFocused}
        className={cn(
          "group flex items-center gap-2 rounded-xl border py-1.5 pr-2 transition-colors",
          depthClass,
          isActive
            ? "border-white/[0.08] bg-white/[0.06] text-foreground"
            : "border-transparent bg-transparent text-muted-foreground hover:border-white/[0.06] hover:bg-white/[0.04] hover:text-foreground",
          isUserTeam && !isActive && "bg-white/[0.02]",
          isDragTarget && "border-white/[0.12] bg-white/[0.06]",
          isFocused && "ring-1 ring-white/20 ring-offset-1 ring-offset-background"
        )}
        draggable={canReparent}
        onClick={() => onFocusTeam(node.id)}
        onFocusCapture={() => onFocusTeam(node.id)}
        onDragStart={() => onDragStart(node.id)}
        onDragEnd={onDragEnd}
        onDragOver={(event) => {
          if (!canDropOn(node.id)) return;
          event.preventDefault();
        }}
        onDragEnter={() => onDragEnter(node.id)}
        onDrop={(event) => {
          if (!canDropOn(node.id)) return;
          event.preventDefault();
          onDrop(node.id);
        }}
      >
        {canShowChildren ? (
          <button
            type="button"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onFocusTeam(node.id);
              onToggle(node.id);
            }}
            aria-label={isExpanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
          >
            <ChevronDown
              className={cn("h-4 w-4 transition-transform", !isExpanded && "-rotate-90")}
            />
          </button>
        ) : (
          <span className="w-6 shrink-0" />
        )}

        <Link
          to={`/teams/${node.id}`}
          onClick={onNavigate}
          aria-current={isActive ? "page" : undefined}
          className="flex min-w-0 flex-1 items-center gap-2"
        >
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
            style={{ backgroundColor: `${hueColor}15` }}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d={iconPath}
                stroke={hueColor}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span
            className={cn(
              "truncate text-sm font-medium",
              isUserTeam && "font-semibold text-foreground"
            )}
          >
            {node.name}
          </span>
        </Link>

        {canAddDiscipline || canManageDiscipline ? (
          <div className="invisible flex max-w-0 shrink-0 items-center gap-1 overflow-hidden opacity-0 transition-[max-width,opacity] duration-150 group-hover:visible group-hover:max-w-40 group-hover:opacity-100 group-focus-within:visible group-focus-within:max-w-40 group-focus-within:opacity-100">
            {canAddDiscipline ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={`Add discipline under ${node.name}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setShowAddDiscipline(true);
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            ) : null}

            {canManageDiscipline ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label={`Edit ${node.name}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setShowEditDiscipline(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  aria-label={`Delete ${node.name}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setShowDeleteDiscipline(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <DisciplineFormModal
        open={showAddDiscipline}
        onOpenChange={setShowAddDiscipline}
        parentId={node.id}
      />

      <DisciplineFormModal
        open={showEditDiscipline}
        onOpenChange={setShowEditDiscipline}
        discipline={node}
      />

      <DeleteDisciplineDialog
        teamId={node.id}
        team={node}
        open={showDeleteDiscipline}
        onOpenChange={setShowDeleteDiscipline}
      />

      {canShowChildren && isExpanded ? (
        <div role="group" className="mt-1 space-y-1">
          {node.children.map((child) => (
            <TeamTreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              currentTeamId={currentTeamId}
              isAdmin={isAdmin}
              canReparent={canReparent}
              userTeamIds={userTeamIds}
              dragOverId={dragOverId}
              focusedTeamId={focusedTeamId}
              onToggle={onToggle}
              onFocusTeam={onFocusTeam}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDragEnter={onDragEnter}
              onDrop={onDrop}
              canDropOn={canDropOn}
              registerRowElement={registerRowElement}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function readStoredExpandedIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === "string")
      : [];
  } catch {
    return [];
  }
}

export function TeamTreeSidebar({ currentTeamId, className, onNavigate }: TeamTreeSidebarProps) {
  const { data: tree, isLoading } = useTeamTree();
  const reparentTeam = useReparentTeam();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isAdmin = useAuthStore((state) => state.user?.isGlobalAdmin ?? false);
  const currentUserId = useAuthStore((state) => state.user?.id ?? "");
  const { data: userTeams = [] } = useUserTeams(currentUserId);
  const [expandedIds, setExpandedIds] = useState<string[]>(() => readStoredExpandedIds());
  const [draggedTeamId, setDraggedTeamId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [focusedTeamId, setFocusedTeamId] = useState<string | null>(currentTeamId ?? null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const rowElementsRef = useRef(new Map<string, HTMLDivElement>());

  const flattenedTree = useMemo(() => flattenTeamTree(tree ?? []), [tree]);
  const nodeMap = useMemo(
    () => new Map(flattenedTree.map((node) => [node.id, node])),
    [flattenedTree]
  );
  const userTeamIds = useMemo(
    () => new Set(userTeams.map((membership) => membership.team.id)),
    [userTeams]
  );
  const descendantMap = useMemo(() => {
    const map = new Map<string, Set<string>>();

    const build = (node: TeamTreeNode): Set<string> => {
      const descendants = new Set<string>();
      node.children.forEach((child) => {
        descendants.add(child.id);
        build(child).forEach((descendantId) => descendants.add(descendantId));
      });
      map.set(node.id, descendants);
      return descendants;
    };

    (tree ?? []).forEach(build);
    return map;
  }, [tree]);

  // Determine which top-level groups the user belongs to
  const accessibleGroups = useMemo(() => {
    if (!tree?.length) return [];
    const groups = tree.filter((node) => node.level === "group");
    if (isAdmin) return groups;

    return groups.filter((group) => {
      if (userTeamIds.has(group.id)) return true;
      const descendants = descendantMap.get(group.id);
      if (!descendants) return false;
      return Array.from(descendants).some((id) => userTeamIds.has(id));
    });
  }, [tree, isAdmin, userTeamIds, descendantMap]);

  // Auto-select group on load, and switch group when currentTeamId moves to a different root
  useEffect(() => {
    if (accessibleGroups.length === 0) return;

    // If a team is currently selected, find its root group
    if (currentTeamId && tree?.length) {
      const lineage = findTeamLineage(tree, currentTeamId);
      if (lineage.length > 0) {
        const rootId = lineage[0];
        const matchingGroup = accessibleGroups.find((g) => g.id === rootId);
        if (matchingGroup) {
          if (matchingGroup.id !== selectedGroupId) {
            setSelectedGroupId(matchingGroup.id);
          }
          return;
        }
      }
    }

    // Fallback: if no group is selected yet, pick the first
    if (!selectedGroupId) {
      setSelectedGroupId(accessibleGroups[0]?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibleGroups, currentTeamId, tree]);

  // The filtered tree shows only children of the selected group
  const displayTree = useMemo(() => {
    if (!tree?.length || !selectedGroupId) return tree ?? [];
    const group = tree.find((g) => g.id === selectedGroupId);
    return group ? group.children : tree;
  }, [tree, selectedGroupId]);

  const filteredTeams = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return null;

    return flattenedTree.filter(
      (node) =>
        node.name.toLowerCase().includes(normalizedQuery) ||
        node.key?.toLowerCase().includes(normalizedQuery)
    );
  }, [flattenedTree, searchQuery]);

  // Auto-expand top-level nodes on first load only
  const hasAutoExpanded = useRef(false);
  useEffect(() => {
    if (hasAutoExpanded.current) return;
    if (!displayTree.length) return;
    if (expandedIds.length > 0) return;
    setExpandedIds(displayTree.map((node) => node.id));
    hasAutoExpanded.current = true;
  }, [expandedIds.length, displayTree]);

  useEffect(() => {
    if (!tree?.length || !currentTeamId) return;
    const lineage = findTeamLineage(tree, currentTeamId).slice(0, -1);
    if (lineage.length === 0) return;
    setExpandedIds((prev) => Array.from(new Set([...prev, ...lineage])));
  }, [currentTeamId, tree]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedIds));
    } catch {
      // Ignore localStorage availability issues.
    }
  }, [expandedIds]);

  const expandedSet = useMemo(() => new Set(expandedIds), [expandedIds]);
  const visibleTeamIds = useMemo(() => {
    const result: string[] = [];

    const walk = (nodes: TeamTreeNode[]) => {
      nodes.forEach((node) => {
        result.push(node.id);
        if (expandedSet.has(node.id) && node.children.length > 0) {
          walk(node.children);
        }
      });
    };

    walk(displayTree);
    return result;
  }, [expandedSet, displayTree]);

  useEffect(() => {
    if (visibleTeamIds.length === 0) {
      setFocusedTeamId(null);
      return;
    }

    if (focusedTeamId && visibleTeamIds.includes(focusedTeamId)) {
      return;
    }

    if (currentTeamId && visibleTeamIds.includes(currentTeamId)) {
      setFocusedTeamId(currentTeamId);
      return;
    }

    setFocusedTeamId(visibleTeamIds[0] ?? null);
  }, [currentTeamId, focusedTeamId, visibleTeamIds]);

  useEffect(() => {
    if (!focusedTeamId || filteredTeams !== null) return;
    rowElementsRef.current.get(focusedTeamId)?.scrollIntoView({ block: "nearest" });
  }, [filteredTeams, focusedTeamId]);

  const setRowElement = (teamId: string, element: HTMLDivElement | null) => {
    if (element) {
      rowElementsRef.current.set(teamId, element);
      return;
    }

    rowElementsRef.current.delete(teamId);
  };

  const toggleExpanded = (teamId: string) => {
    setExpandedIds((prev) =>
      prev.includes(teamId) ? prev.filter((value) => value !== teamId) : [...prev, teamId]
    );
  };

  const handleTreeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (visibleTeamIds.length === 0) return;

    const activeTeamId =
      focusedTeamId && visibleTeamIds.includes(focusedTeamId)
        ? focusedTeamId
        : currentTeamId && visibleTeamIds.includes(currentTeamId)
          ? currentTeamId
          : visibleTeamIds[0];

    if (!activeTeamId) return;

    const currentIndex = visibleTeamIds.indexOf(activeTeamId);
    const currentNode = nodeMap.get(activeTeamId);

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        const nextTeamId = visibleTeamIds[currentIndex + 1];
        if (nextTeamId) {
          setFocusedTeamId(nextTeamId);
        }
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        const previousTeamId = visibleTeamIds[currentIndex - 1];
        if (previousTeamId) {
          setFocusedTeamId(previousTeamId);
        }
        break;
      }
      case "ArrowRight": {
        if (!currentNode) return;
        event.preventDefault();
        if (currentNode.children.length > 0 && !expandedSet.has(activeTeamId)) {
          setExpandedIds((prev) => (prev.includes(activeTeamId) ? prev : [...prev, activeTeamId]));
        } else if (currentNode.children[0]) {
          setFocusedTeamId(currentNode.children[0].id);
        }
        break;
      }
      case "ArrowLeft": {
        if (!currentNode) return;
        event.preventDefault();
        if (currentNode.children.length > 0 && expandedSet.has(activeTeamId)) {
          setExpandedIds((prev) => prev.filter((value) => value !== activeTeamId));
        } else if (currentNode.parentId && visibleTeamIds.includes(currentNode.parentId)) {
          setFocusedTeamId(currentNode.parentId);
        }
        break;
      }
      case "Enter": {
        event.preventDefault();
        navigate(`/teams/${activeTeamId}`);
        onNavigate?.();
        break;
      }
      case "Home": {
        event.preventDefault();
        setFocusedTeamId(visibleTeamIds[0] ?? null);
        break;
      }
      case "End": {
        event.preventDefault();
        setFocusedTeamId(visibleTeamIds[visibleTeamIds.length - 1] ?? null);
        break;
      }
      default:
        break;
    }
  };

  const canDropOn = (parentId: string | null) => {
    if (!isAdmin || !draggedTeamId) return false;
    if (parentId === draggedTeamId) return false;
    if (parentId && descendantMap.get(draggedTeamId)?.has(parentId)) return false;

    const draggedNode = nodeMap.get(draggedTeamId);
    if (!draggedNode?.level) return Boolean(parentId === null || parentId);
    if (parentId === null) return draggedNode.level === "group";

    const parentNode = nodeMap.get(parentId);
    if (!parentNode?.level) return false;
    return TEAM_LEVEL_ORDER[parentNode.level] < TEAM_LEVEL_ORDER[draggedNode.level];
  };

  const handleDrop = async (parentId: string | null) => {
    if (!draggedTeamId || !canDropOn(parentId)) return;

    try {
      await reparentTeam.mutateAsync({ id: draggedTeamId, parentId });
      toast(parentId ? "Team moved in hierarchy" : "Team moved to the top level");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to move team");
    } finally {
      setDraggedTeamId(null);
      setDragOverId(null);
    }
  };

  return (
    <aside className={cn("glass-card rounded-2xl p-4", className)}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <FolderTree className="h-4 w-4 text-primary" />
            Team hierarchy
          </div>
        </div>
        {expandedIds.length > 0 && filteredTeams === null ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={() => setExpandedIds([])}
          >
            Collapse all
          </Button>
        ) : null}
      </div>

      {/* Group selector dropdown — only shown if user has access to multiple groups */}
      {accessibleGroups.length > 1 ? (
        <div className="relative mb-3">
          <button
            type="button"
            onClick={() => setGroupDropdownOpen((prev) => !prev)}
            className="flex w-full items-center gap-2.5 rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-white/[0.06]"
          >
            {(() => {
              const selected = accessibleGroups.find((g) => g.id === selectedGroupId);
              if (!selected) return null;
              const hueColor = getTeamHueColor(selected.name);
              const iconPath = getTeamIconPath(selected.name, "group");
              return (
                <>
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
                    style={{ backgroundColor: `${hueColor}15` }}
                  >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d={iconPath} stroke={hueColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="flex-1 truncate text-left">{selected.name}</span>
                </>
              );
            })()}
            <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", groupDropdownOpen && "rotate-180")} />
          </button>
          {groupDropdownOpen ? (
            <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-white/[0.08] bg-background shadow-lg">
              {accessibleGroups.map((group) => {
                const isSelected = selectedGroupId === group.id;
                const hueColor = getTeamHueColor(group.name);
                const iconPath = getTeamIconPath(group.name, "group");
                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => {
                      setSelectedGroupId(group.id);
                      setGroupDropdownOpen(false);
                    }}
                    className={cn(
                      "flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors",
                      isSelected
                        ? "bg-white/[0.06] font-medium text-foreground"
                        : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"
                    )}
                  >
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
                      style={{ backgroundColor: `${hueColor}15` }}
                    >
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d={iconPath} stroke={hueColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="truncate">{group.name}</span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mb-3 space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search teams..."
            aria-label="Search teams"
            className="!pl-11"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="h-12 animate-pulse rounded-xl bg-muted/60" />
          ))}
        </div>
      ) : !displayTree.length ? (
        <div className="rounded-xl border border-dashed border-white/[0.08] px-4 py-6 text-sm text-muted-foreground">
          No teams available yet.
        </div>
      ) : filteredTeams !== null ? (
        filteredTeams.length > 0 ? (
          <div className="space-y-1">
            {filteredTeams.map((teamNode) => {
              const hueColor = getTeamHueColor(teamNode.name);
              const iconPath = getTeamIconPath(teamNode.name, teamNode.level);
              const isActive = currentTeamId === teamNode.id;
              const isUserTeam = userTeamIds.has(teamNode.id);

              return (
                <Link
                  key={teamNode.id}
                  to={`/teams/${teamNode.id}`}
                  onClick={onNavigate}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-2 rounded-xl border px-2 py-2 transition-colors",
                    isActive
                      ? "border-white/[0.08] bg-white/[0.06] text-foreground"
                      : "border-transparent text-muted-foreground hover:border-white/[0.06] hover:bg-white/[0.04] hover:text-foreground",
                    isUserTeam && !isActive && "bg-white/[0.02]"
                  )}
                >
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
                    style={{ backgroundColor: `${hueColor}15` }}
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path
                        d={iconPath}
                        stroke={hueColor}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span
                    className={cn(
                      "truncate text-sm font-medium",
                      isUserTeam && "font-semibold text-foreground"
                    )}
                  >
                    {teamNode.name}
                  </span>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-white/[0.08] px-4 py-6 text-sm text-muted-foreground">
            No teams match “{searchQuery.trim()}”.
          </div>
        )
      ) : (
        <div
          tabIndex={0}
          role="tree"
          aria-label="Team hierarchy"
          aria-activedescendant={focusedTeamId ? getTreeItemId(focusedTeamId) : undefined}
          className={cn(
            "space-y-1 rounded-2xl border border-transparent p-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            dragOverId === "root" && canDropOn(null) && "border-white/[0.12] bg-white/[0.04]"
          )}
          onFocus={() => {
            if (!focusedTeamId && visibleTeamIds.length > 0) {
              setFocusedTeamId(
                currentTeamId && visibleTeamIds.includes(currentTeamId)
                  ? currentTeamId
                  : (visibleTeamIds[0] ?? null)
              );
            }
          }}
          onKeyDown={handleTreeKeyDown}
          onDragOver={(event) => {
            if (!canDropOn(null)) return;
            event.preventDefault();
          }}
          onDragEnter={() => setDragOverId("root")}
          onDrop={(event) => {
            if (!canDropOn(null)) return;
            event.preventDefault();
            void handleDrop(null);
          }}
        >
          {isAdmin && draggedTeamId ? (
            <div className="mb-2 rounded-xl border border-dashed border-white/[0.08] bg-white/[0.02] px-3 py-2 text-xs text-muted-foreground">
              Drop on a team to reparent, or drop here to move a group to the top level.
            </div>
          ) : null}
          {displayTree.map((node) => (
            <TeamTreeRow
              key={node.id}
              node={node}
              depth={0}
              expandedIds={expandedSet}
              currentTeamId={currentTeamId}
              isAdmin={isAdmin}
              canReparent={isAdmin}
              userTeamIds={userTeamIds}
              dragOverId={dragOverId}
              focusedTeamId={focusedTeamId}
              onToggle={toggleExpanded}
              onFocusTeam={setFocusedTeamId}
              onDragStart={(teamId) => setDraggedTeamId(teamId)}
              onDragEnd={() => {
                setDraggedTeamId(null);
                setDragOverId(null);
              }}
              onDragEnter={(teamId) => setDragOverId(teamId)}
              onDrop={(parentId) => {
                void handleDrop(parentId);
              }}
              canDropOn={canDropOn}
              registerRowElement={setRowElement}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </aside>
  );
}
